import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Checkbox, Alert, Button,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { providersAPI, pricingAPI, powerAddonsAPI, regionsAPI } from '../../../services/api';
import { Provider, Region, PowerAddon, PowerAddonPricingRow } from '../../../types';

interface PriceChangeMap {
  [pricingId: string]: { monthlyCost?: number; isAvailable?: boolean };
}

const PowerAddonPricing = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState(0);
  const [powerAddons, setPowerAddons] = useState<PowerAddon[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [pricing, setPricing] = useState<PowerAddonPricingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [changes, setChanges] = useState<PriceChangeMap>({});

  const fetchInitialData = async () => {
    try {
      const [providersRes, addonsRes] = await Promise.all([providersAPI.getAll(), powerAddonsAPI.getAll()]);
      const ibmProviders = providersRes.data.filter((p: Provider) => p.IsActive && p.ProviderName.toLowerCase().includes('ibm'));
      setProviders(ibmProviders);
      setPowerAddons(addonsRes.data.filter((a: PowerAddon) => a.IsActive));
    } catch { setError('Failed to load data'); } finally { setLoading(false); }
  };

  const fetchPricingData = useCallback(async () => {
    if (providers.length === 0) return;
    try {
      const currentProvider = providers[selectedProvider];
      if (!currentProvider) return;
      const [pricingRes, regionsRes] = await Promise.all([
        pricingAPI.getPowerAddonPricing(currentProvider.ProviderId),
        regionsAPI.getAll(currentProvider.ProviderId),
      ]);
      setRegions(regionsRes.data.filter((r: Region) => r.IsActive));
      setPricing(pricingRes.data);
      setChanges({});
    } catch { setError('Failed to load pricing'); }
  }, [providers, selectedProvider]);

  useEffect(() => { fetchInitialData(); }, []);
  useEffect(() => { if (providers.length > 0) fetchPricingData(); }, [selectedProvider, providers, fetchPricingData]);

  const handlePriceChange = (pricingId: number, field: string, value: number | boolean) => {
    setChanges({ ...changes, [pricingId]: { ...changes[pricingId], [field]: value } });
  };

  const handleSaveChanges = async () => {
    try {
      const updates = Object.entries(changes).map(([pricingId, changedFields]) => {
        const original = pricing.find(p => p.PowerAddonPricingId === parseInt(pricingId));
        return pricingAPI.updatePowerAddonPrice(pricingId, {
          monthlyCost: changedFields.monthlyCost !== undefined ? changedFields.monthlyCost : original?.MonthlyCost ?? 0,
          isAvailable: changedFields.isAvailable !== undefined ? changedFields.isAvailable : original?.IsAvailable ?? 0,
        });
      });
      await Promise.all(updates);
      setSuccess('Prices updated successfully');
      setChanges({});
      fetchPricingData();
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Failed to update prices'); }
  };

  const getPriceValue = (pricingId: number, defaultValue: number) =>
    changes[pricingId]?.monthlyCost !== undefined ? changes[pricingId]!.monthlyCost! : defaultValue;

  const getAvailableValue = (pricingId: number, defaultValue: number) =>
    changes[pricingId]?.isAvailable !== undefined ? changes[pricingId]!.isAvailable! : !!defaultValue;

  if (loading) return <Typography>Loading...</Typography>;
  if (providers.length === 0) return <Box><Typography variant="h4" sx={{ mb: 2 }}>Power Addon Pricing</Typography><Alert severity="info">No IBM providers configured. Power addon pricing is only available for IBM Cloud providers.</Alert></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Power Addon Pricing</Typography>
        <Button variant="contained" color="secondary" startIcon={<SaveIcon />} onClick={handleSaveChanges} disabled={Object.keys(changes).length === 0}>
          Save Changes ({Object.keys(changes).length})
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {providers.length > 1 && (
        <Paper sx={{ mb: 2 }}>
          <Tabs value={selectedProvider} onChange={(_e, v: number) => setSelectedProvider(v)} variant="fullWidth">
            {providers.map((p) => <Tab key={p.ProviderId} label={p.ProviderName} />)}
          </Tabs>
        </Paper>
      )}
      {powerAddons.map((addon) => {
        const entries = pricing.filter(p => p.PowerAddonId === addon.PowerAddonId);
        return (
          <Paper key={addon.PowerAddonId} sx={{ mb: 3, p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {addon.PowerAddonName}
              {addon.Description && <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5, fontWeight: 400 }}>&ndash; {addon.Description}</Typography>}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Field</TableCell>
                    {regions.map((r) => <TableCell key={r.RegionId} align="center">{r.RegionName}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Monthly Cost ($)</TableCell>
                    {regions.map((r) => {
                      const entry = entries.find(e => e.RegionId === r.RegionId);
                      if (!entry) return <TableCell key={r.RegionId} />;
                      return (
                        <TableCell key={r.RegionId} align="center">
                          <TextField type="number" size="small" value={getPriceValue(entry.PowerAddonPricingId, entry.MonthlyCost)}
                            onChange={(e) => handlePriceChange(entry.PowerAddonPricingId, 'monthlyCost', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                            inputProps={{ step: 0.01, min: 0 }} sx={{ width: 100, '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5, px: 1 } }} />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell>Enabled</TableCell>
                    {regions.map((r) => {
                      const entry = entries.find(e => e.RegionId === r.RegionId);
                      if (!entry) return <TableCell key={r.RegionId} />;
                      return (
                        <TableCell key={r.RegionId} align="center">
                          <Checkbox checked={getAvailableValue(entry.PowerAddonPricingId, entry.IsAvailable) as boolean}
                            onChange={(e) => handlePriceChange(entry.PowerAddonPricingId, 'isAvailable', e.target.checked)} />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })}
    </Box>
  );
};

export default PowerAddonPricing;
